export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

type KeyEvent = Pick<KeyboardEvent, 'key' | 'shiftKey' | 'preventDefault'>

/**
 * Keeps Tab and Shift+Tab inside a dialog: from the last control back to the first, and the other way. The focused
 * element counts even when it is out of the Tab order (e.g. reached with arrow keys).
 */
export function trapTab(event: KeyEvent, container: HTMLElement | null): void {
  if (event.key !== 'Tab' || !container) return
  const items = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el === document.activeElement || (el.tabIndex >= 0 && el.offsetParent !== null),
  )
  if (items.length === 0) return
  const first = items[0]
  const last = items[items.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

/** Arrow keys (and Home and End) move focus between items, e.g. in a menu, wrapping round at the ends. */
export function moveFocus(event: KeyEvent, items: HTMLElement[], keys: { next: string; previous: string }): void {
  if (items.length === 0) return
  const current = items.indexOf(document.activeElement as HTMLElement)
  let index: number
  if (event.key === 'Home') index = 0
  else if (event.key === 'End') index = items.length - 1
  else if (event.key === keys.next) index = current + 1
  else if (event.key === keys.previous) index = current < 0 ? items.length - 1 : current - 1
  else return
  event.preventDefault()
  items[(index + items.length) % items.length].focus()
}

/** Whether an element shows a focus ring now (`:focus-visible`); true where the browser cannot say, which keeps the ring. */
export function showsFocusRing(element: Element | null): boolean {
  if (!element) return false
  try {
    return element.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * Gives focus back to what had it before a dialog opened, with a ring only if it had one then. On iOS a tap focuses the
 * nearest focusable thing round the button (a message row) without a ring, and WebKit rings whatever is focused by script,
 * its own restore as a dialog closes included: so that is let go of first and focused again with `focusVisible: false`. Nothing
 * happens when the element has left the page.
 */
export function returnFocus(element: HTMLElement | null, ring: boolean): void {
  // Gone with whatever closed (a button on a page closing at the same time): letting go of the focused element would be all it did.
  if (!element?.isConnected) return
  if (ring) {
    element.focus()
    return
  }
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
  element.focus({ focusVisible: false } as FocusOptions)
}
